import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { SubscriptionGate } from '@/components/paywall/SubscriptionGate';
import { usePremiumAccess } from '@/hooks/usePremiumAccess';
import { track } from '@/lib/analytics';
import { learnerApiJson } from '@/lib/learner-api';
import { stickerPackGateCopy } from '@/lib/paywall';
import { useLearnerAuth } from '@/providers/LearnerAuth';
import { typeScale } from '@/lib/typography';

type Pack = {
  id: number;
  slug: string;
  name: string;
  cover_image_url: string | null;
  stickers: Array<{ id: number; name: string; image_url: string }>;
};

type SceneSummary = {
  id: string;
  title: string;
  updated_at: string;
  placement_count: number;
};

export const Route = createFileRoute('/app/stickers/')({ component: StickersPage });

function StickersPage() {
  const auth = useLearnerAuth();
  const premium = usePremiumAccess();
  const [gatePack, setGatePack] = useState<Pack | null>(null);
  const packs = useQuery({
    queryKey: ['sticker-packs'],
    queryFn: () => learnerApiJson<Pack[]>(auth?.getToken ?? null, '/v1/sticker-packs'),
  });
  const scenes = useQuery({
    queryKey: ['sticker-scenes'],
    queryFn: () => learnerApiJson<SceneSummary[]>(auth?.getToken ?? null, '/v1/user/sticker-scenes?summary=1', { auth: true }),
    enabled: Boolean(auth?.isSignedIn),
  });
  const gate = stickerPackGateCopy();

  return (
    <div className="mx-auto max-w-5xl px-5 py-8 sm:px-8">
      <div className="flex items-end justify-between">
        <div>
          <h1 className={typeScale.screenTitle}>Stickers</h1>
          <p className="mt-2 text-slate-600">Build a Hmong vocabulary scene or postcard.</p>
        </div>
        <Link
          to="/app/stickers/scenes/$sceneId"
          params={{ sceneId: 'new' }}
          className="rounded-2xl bg-[#399918] px-5 py-3 font-bold text-white"
        >
          New scene
        </Link>
      </div>
      {auth?.isSignedIn && scenes.data?.length ? (
        <section className="mt-8">
          <h2 className="text-xl font-extrabold">Your scenes</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {scenes.data.map((scene) => (
              <Link
                key={scene.id}
                to="/app/stickers/scenes/$sceneId"
                params={{ sceneId: scene.id }}
                className="rounded-3xl border border-[#cfe8c6] bg-white p-5 hover:border-[#399918]"
              >
                <h3 className="text-lg font-extrabold">{scene.title}</h3>
                <p className="text-sm text-slate-500">{scene.placement_count} stickers</p>
              </Link>
            ))}
          </div>
        </section>
      ) : null}
      <h2 className="mt-10 text-xl font-extrabold">Packs</h2>
      <div className="mt-4 grid gap-5 sm:grid-cols-2">
        {packs.data?.map((pack) => (
          <button
            key={pack.id}
            type="button"
            onClick={() => {
              track('Sticker Pack Screen', { pack: pack.name });
              if (premium.isReady && !premium.isSubscribed) {
                setGatePack(pack);
                return;
              }
            }}
            className="rounded-3xl border border-[#cfe8c6] bg-white p-5 text-left hover:border-[#399918]"
          >
            {pack.cover_image_url ? (
              <img src={pack.cover_image_url} alt="" className="h-36 w-full rounded-2xl object-contain" />
            ) : null}
            <h2 className="mt-4 text-xl font-extrabold">{pack.name}</h2>
            <p className="text-sm text-slate-500">{pack.stickers.length} stickers</p>
          </button>
        ))}
      </div>
      <SubscriptionGate
        source={gate.source}
        content={gatePack?.slug}
        message={gate.message}
        stickerImageUrls={gatePack?.stickers.map((sticker) => sticker.image_url) ?? []}
        variant="modal"
        open={Boolean(gatePack)}
        onDismiss={() => setGatePack(null)}
      />
    </div>
  );
}
